"use client";
import { useState } from "react";
import ReactHtmlParser from "react-html-parser";

import { dateFormatter } from "@stadium/helpers/dayjs";

import TippyTooltip from "@stadium/ui/src/components/tooltip/tooltip";

import { standardDateFormatFullYear } from "@stadium/constants";

import { StyledButton } from "@stadium/ui/src/components/button/styled-button";

import Button from "@stadium/ui/src/components/button/button";

import { useDownloadInvoice } from "@stadium/checkout/src/hooks/use-fetch-order-invoice";
import { useConfig } from "@stadium/hooks/lib/use-config";
import {
  getPlatformFeeInfoMessage,
  getPlatformFeeLabel,
} from "@stadium/redux-utils/selectors/configSelector";

import upStyle from "./main.module.scss";

const emptyObj = {};

const OrderSummary = ({
  summaryTableTitle = "",
  orderData = emptyObj,
  showDownloadCta = true,
  footerClass = "",
  tableWraperClass = "",
}) => {
  const {
    orderNumber,
    orderCompletionDate,
    refNo = "",
    checkoutDetails,
    totalPaid,
    checkoutNote,
  } = orderData;
  const platformFeeLabel = useConfig(getPlatformFeeLabel);
  const platformFeeTooltipText = useConfig(getPlatformFeeInfoMessage);

  const { loading, fetchFile } = useDownloadInvoice(orderNumber);

  const [isExpanded, setIsExpanded] = useState(false);

  const renderTooltipContent = (tooltipContent) => {
    return (
      <TippyTooltip
        arrow
        infoIcon
        isLightTheme
        content={
          <span style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
            {ReactHtmlParser(tooltipContent)}
          </span>
        }
        placement="top"
        customClassName={`${upStyle.platformTippyText} ${upStyle.tooltipWrapper}`}
        containerClass={upStyle.tooltipContainer}
        childrenWrapperClassName={upStyle.tippyParent}
      />
    );
  };

  return (
    <div className={`${upStyle.tableWrap} ${tableWraperClass}`}>
      <table className={upStyle.tableExpand}>
        <tbody>
          <tr className={upStyle.orderSummary}>
            <td className={upStyle.orderSummaryTitle}>{summaryTableTitle}</td>
            {showDownloadCta && (
              <td className={upStyle.downloadIcon}>
                <TippyTooltip
                  content="Download Invoice"
                  isLightTheme={true}
                  arrow={false}
                  placement="bottom"
                  customClassName={`${upStyle.invoceTippyText} ${upStyle.tooltipWrapper}`}
                  containerClass={upStyle.tooltipContainer}
                >
                  <span className={upStyle.downloadIconHolder}>
                    <StyledButton
                      disableOnLoad
                      styleType="icon-btn"
                      isLoading={loading}
                      onClick={fetchFile}
                    >
                      <span className={upStyle.tooltipIconInfo} />
                    </StyledButton>
                  </span>
                </TippyTooltip>
              </td>
            )}
          </tr>
          <tr className={`${upStyle.orderTotal} ${upStyle.orderDate}`}>
            <td>
              {dateFormatter(orderCompletionDate, {
                format: standardDateFormatFullYear,
              })}
            </td>
            {!!refNo && <td>Ref: {refNo}</td>}
          </tr>
          {isExpanded && (
            <>
              {Object.values(checkoutDetails).map(
                ({ key, value, tooltipContent, detail }) =>
                  !!value && (
                    <tr className={upStyle.orderTotal} key={key}>
                      <td className={`${upStyle.orderTotalTitle} ${upStyle.leftAlign}`}>
                        {key}{" "}
                        {key === platformFeeLabel && <span className={upStyle.asterisk}>*</span>}
                        {tooltipContent && renderTooltipContent(tooltipContent)}
                      </td>
                      <td className={upStyle.dataContainer}>
                        {ReactHtmlParser(value)}{" "}
                        {detail && <div className={upStyle.chargedContent}>{detail}</div>}
                      </td>
                    </tr>
                  ),
              )}
            </>
          )}
          {totalPaid ? (
            <tr className={`${upStyle.orderTotal} ${upStyle.orderTotalrow}`}>
              <td className={upStyle.orderTotalTitle}>Total paid</td>
              <td className={upStyle.fwBold}>{totalPaid}</td>
            </tr>
          ) : (
            <></>
          )}
          {Object.values(checkoutDetails)?.some(({ key }) => key === platformFeeLabel) &&
            platformFeeTooltipText && (
              <tr className={upStyle.platformTippyTextMobile}>
                <td>*{ReactHtmlParser(platformFeeTooltipText)}</td>
              </tr>
            )}
          {isExpanded && checkoutNote && (
            <tr>
              <td>
                <div className={upStyle.checkoutNote}>{checkoutNote}</div>
              </td>
            </tr>
          )}
        </tbody>
        <tfoot className={`${upStyle.tFootWrap} ${footerClass}`}>
          <Button customClass={upStyle.expandLink} onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? "Collapse" : "Expand"}
          </Button>
        </tfoot>
      </table>
    </div>
  );
};

export default OrderSummary;
